<template>
  <div v-if="content || placeholder || video" class="product-intro" :class="introTypeClass">
    <div class="product-intro__inner">
      <div v-if="content || placeholder || video" class="product-intro__content" :class="{ 'product-intro__content_opened': contentState }">
        <div class="product-intro__col-holder" ref="productIntroColHolder" v-resize="getContentHeight">
          <div v-if="content" class="product-intro__col">
            <product-content :content="content" />
          </div>
          <div v-if="placeholder || video" class="product-intro__col">
            <media :placeholder="placeholder" :video="video" />
          </div>
        </div>
        <div v-if="isContentCut" class="product-intro__content-more-link">
          <span @click="toggleContent">
            {{ contentState ? langJson[lang].seeLess : langJson[lang].seeMore }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { useMainStore } from '@/stores/main';
  import { privacyTypes } from '~/constants';

  interface ResourceItem {
    id: string;
    mimeType: string;
    fileName: string;
    [key: string]: any;
  }

  interface ProductInfo {
    external?: any;
    internal?: any;
    erpCode?: string;
    serviceInfo?: any;
    publishedTo?: number | string;
    resources?: ResourceItem[];
  }

  interface ContentData {
    external?: any;
    internal?: any;
    erpCode?: string;
    serviceInfo?: any;
  }

  const props = defineProps<{
    productInfo?: ProductInfo;
  }>();

  const mainStore = useMainStore();
  const langJson = computed(() => mainStore.langJson);
  const lang = computed(() => mainStore.lang);

  const contentHeight = ref<number>(0);
  const maxContentHeight = ref<number>(420);
  const contentState = ref<boolean>(false);
  const productIntroColHolder = ref<HTMLElement | null>(null);

  // Section-level internal/service modifier — drives the left borderline.
  // Mirrors PageItem's accordionItemTypeClass so the line aligns across sections.
  const introTypeClass = computed((): string => {
    switch (Number(props.productInfo?.publishedTo)) {
      case privacyTypes.INTERNAL:
        return 'product-intro_internal';
      case privacyTypes.SERVICE:
        return 'product-intro_service';
      default:
        // External and any unknown/missing value: no rail.
        return '';
    }
  });

  const content = computed((): ContentData => {
    const c: ContentData = {};
    c.external = props.productInfo?.external;
    c.internal = props.productInfo?.internal;
    c.erpCode = props.productInfo?.erpCode;
    c.serviceInfo = props.productInfo?.serviceInfo;
    return c;
  });

  const isResourcesPresent = computed((): boolean => !!props.productInfo?.resources?.length);

  const video = computed((): ResourceItem | undefined => {
    if (isResourcesPresent.value && props.productInfo?.resources) {
      const videos = props.productInfo.resources.filter((item) => {
        return item.mimeType.indexOf('application/octet-stream') > -1 && item.fileName.split('.').pop() != 'dwg';
      });
      return videos[0];
    }
    return undefined;
  });

  const placeholder = computed((): ResourceItem | undefined => {
    if (isResourcesPresent.value && props.productInfo?.resources) {
      const images = props.productInfo.resources.filter((item) => {
        return item.mimeType.indexOf('image') > -1;
      });
      return images[0];
    }
    return undefined;
  });

  const isContentCut = computed((): boolean => contentHeight.value > maxContentHeight.value);

  function getContentHeight(): void {
    nextTick(() => {
      if (productIntroColHolder.value) {
        contentHeight.value = productIntroColHolder.value.clientHeight;
      }
    });
  }

  function toggleContent(): void {
    contentState.value = !contentState.value;
  }

  onMounted(() => {
    getContentHeight();
  });

  // Watch for content changes and recalculate height
  watch(
    [content, placeholder, video],
    () => {
      nextTick(() => {
        getContentHeight();
      });
    },
    { deep: true }
  );
</script>
